import React, { useState, useEffect, useReducer, useRef, useContext, useMemo } from "react";
import { isSameDay, parseISO, format } from "date-fns";
import clsx from "clsx";
import { green } from "@mui/material/colors";
import { makeStyles } from "@mui/styles";
import {
  Button,
  CircularProgress,
  Divider,
  IconButton,
  Checkbox,
} from "@mui/material";
import {
  AccessTime,
  Block,
  Done,
  DoneAll,
  InsertDriveFile,
  ExpandMore,
  GetApp,
  PictureAsPdf,
  Star,
} from "@mui/icons-material";

import MarkdownWrapper from "../MarkdownWrapper";
import VcardPreview from "../VcardPreview";
import LocationPreview from "../LocationPreview";
import ModalImageCors from "../ModalImageCors";
import MessageOptionsMenu from "../MessageOptionsMenu";
import whatsBackground from "../../assets/wa-background.png";
import whatsBackgroundDark from "../../assets/wa-background_black.png";
import { ReplyMessageContext } from "../../context/ReplyingMessage/ReplyingMessageContext";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import Audio from "../Audio";
import { toast } from "react-toastify";
import { useSocket } from "../../context/Socket/SocketContext";

const useStyles = makeStyles((theme) => ({
  messagesListWrapper: {
    overflow: "hidden",
    position: "relative",
    display: "flex",
    flexDirection: "column",
    flexGrow: 1,
  },

  messagesList: {
    backgroundImage: theme.palette.mode === 'dark' ? `url(${whatsBackgroundDark})` : `url(${whatsBackground})`,
    backgroundColor: theme.palette.mode === 'dark' ? '#0b141a' : '#efeae2',
    backgroundSize: "400px",
    backgroundRepeat: "repeat",
    display: "flex",
    flexDirection: "column",
    flexGrow: 1,
    padding: "16px 12px",
    overflowY: "auto",
    overflowX: "hidden",
    [theme.breakpoints.down("md")]: {
      padding: "14px 10px",
    },
    [theme.breakpoints.down("sm")]: {
      padding: "12px 8px 90px",
    },
    ...theme.scrollbarStyles,
  },

  circleLoading: {
    color: "#00a884",
    position: "absolute",
    opacity: "70%",
    top: 0,
    left: "50%",
    marginTop: 12,
  },

  messageLeft: {
    marginRight: "auto",
    marginTop: 2,
    minWidth: 150,
    maxWidth: "96%",
    height: "auto",
    display: "block",
    width: "auto",
    position: "relative",
    backgroundColor: theme.palette.mode === 'dark' ? "#2a3942" : "#ffffff",
    color: theme.palette.mode === 'dark' ? "#e9edef" : "#111b21",
    alignSelf: "flex-start",
    borderRadius: "0 8px 8px 8px",
    padding: "5px 9px 5px 9px",
    boxShadow: "0 1px 0.5px rgba(0, 0, 0, 0.13)",
    fontSize: "0.9rem",
    lineHeight: 1.4,
    wordBreak: "normal",
    overflowWrap: "anywhere",
    [theme.breakpoints.down("md")]: {
      maxWidth: "97%",
    },
    [theme.breakpoints.down("sm")]: {
      maxWidth: "98%",
      fontSize: "1rem",
    },
    "& audio, & video, & img": {
      maxWidth: "100%",
      display: "block",
    },
    "& .MuiButton-root": {
      maxWidth: "100%",
    },
    "&:before": {
      content: '""',
      position: "absolute",
      top: 0,
      left: -8,
      width: 8,
      height: 10,
      backgroundColor: theme.palette.mode === 'dark' ? "#2a3942" : "#ffffff",
      clipPath: "polygon(100% 0, 0 0, 100% 100%)",
    },
    "&:hover #messageActionsButton": {
      display: "flex",
      position: "absolute",
      top: 0,
      right: 0,
    },
  },

  mediaMessage: {
    minWidth: 0,
    width: "fit-content",
    maxWidth: "min(420px, 92%)",
    padding: "5px 7px 5px 7px",
    [theme.breakpoints.down("sm")]: {
      maxWidth: "86vw",
    },
  },

  fileMessage: {
    minWidth: 0,
    width: "fit-content",
    maxWidth: "min(360px, 92%)",
    padding: "5px 7px 5px 7px",
    [theme.breakpoints.down("sm")]: {
      maxWidth: "86vw",
    },
  },

  quotedContainerLeft: {
    margin: "-3px -3px 6px -6px",
    overflow: "hidden",
    backgroundColor: theme.palette.mode === 'dark' ? "#1d232a" : "#f0f0f0",
    borderRadius: "7.5px",
    display: "flex",
    position: "relative",
    cursor: "pointer",
  },

  quotedMsg: {
    padding: 10,
    maxWidth: "100%",
    height: "auto",
    display: "flex",
    flexDirection: "column",
    whiteSpace: "pre-wrap",
    overflow: "hidden",
    fontSize: "0.8rem",
    color: theme.palette.mode === "dark" ? "#d1d7db" : "#111b21",
  },

  quotedMediaThumb: {
    width: 72,
    height: 72,
    objectFit: "cover",
    borderRadius: 6,
    display: "block",
    marginTop: 4,
    marginBottom: 4,
  },

  quotedMediaText: {
    display: "block",
    fontSize: "0.75rem",
    opacity: 0.85,
    marginTop: 2,
    color: theme.palette.mode === "dark" ? "#aebac1" : "#667781",
  },

  quotedSideColorLeft: {
    flex: "none",
    width: "3px",
    backgroundColor: "#6bcbef",
  },

  messageRight: {
    marginLeft: "auto",
    marginTop: 2,
    marginBottom: 2,
    minWidth: 150,
    maxWidth: "96%",
    height: "auto",
    display: "block",
    width: "auto",
    position: "relative",
    backgroundColor: theme.palette.mode === 'dark' ? "#005c4b" : "#d9fdd3",
    color: theme.palette.mode === 'dark' ? "#e9edef" : "#111b21",
    alignSelf: "flex-end",
    borderRadius: "8px 0 8px 8px",
    padding: "5px 9px 5px 9px",
    boxShadow: "0 1px 0.5px rgba(0, 0, 0, 0.13)",
    fontSize: "0.9rem",
    lineHeight: 1.4,
    wordBreak: "normal",
    overflowWrap: "anywhere",
    [theme.breakpoints.down("md")]: {
      maxWidth: "97%",
    },
    [theme.breakpoints.down("sm")]: {
      maxWidth: "98%",
      fontSize: "1rem",
    },
    "& audio, & video, & img": {
      maxWidth: "100%",
      display: "block",
    },
    "& .MuiButton-root": {
      maxWidth: "100%",
    },
    "&:before": {
      content: '""',
      position: "absolute",
      top: 0,
      right: -8,
      width: 8,
      height: 10,
      backgroundColor: theme.palette.mode === 'dark' ? "#005c4b" : "#d9fdd3",
      clipPath: "polygon(0 0, 100% 0, 0 100%)",
    },
    "&:hover #messageActionsButton": {
      display: "flex",
      position: "absolute",
      top: 0,
      right: 0,
    },
  },

  quotedContainerRight: {
    margin: "-3px -3px 6px -6px",
    overflowY: "hidden",
    backgroundColor: theme.palette.mode === 'dark' ? "#012e2c" : "#cfe9ba",
    borderRadius: "7.5px",
    display: "flex",
    position: "relative",
    cursor: "pointer",
  },

  quotedMsgRight: {
    padding: 10,
    maxWidth: "100%",
    height: "auto",
    whiteSpace: "pre-wrap",
    color: theme.palette.mode === "dark" ? "#e9edef" : "#111b21",
  },

  quotedSideColorRight: {
    flex: "none",
    width: "3px",
    backgroundColor: "#35cd96",
  },

  messageActionsButton: {
    display: "none",
    position: "relative",
    color: "#999",
    zIndex: 1,
    backgroundColor: "inherit",
    opacity: "90%",
    "&:hover, &.Mui-focusVisible": { backgroundColor: "inherit" },
  },

  highlightedMessage: {
    position: "relative",
  },

  highlightedStar: {
    position: "absolute",
    top: 4,
    right: 6,
    fontSize: "0.85rem",
    color: "#00a884",
    opacity: 0.95,
    pointerEvents: "none",
    zIndex: 2,
  },

  focusedMessage: {
    animation: "$focusPulse 1.4s ease",
  },

  "@keyframes focusPulse": {
    "0%": {
      boxShadow: "0 0 0 0 rgba(37, 211, 102, 0.55)",
    },
    "70%": {
      boxShadow: "0 0 0 10px rgba(37, 211, 102, 0)",
    },
    "100%": {
      boxShadow: "0 0 0 0 rgba(37, 211, 102, 0)",
    },
  },

  messageContactName: {
    display: "block",
    color: "#6bcbef",
    fontWeight: 500,
    fontSize: "0.75rem",
    marginBottom: 1,
    lineHeight: 1.2,
  },

  textContentItem: {
    display: "block",
    wordBreak: "normal",
    overflowWrap: "anywhere",
    padding: "0",
    marginTop: 2,
    "& > p": {
      margin: 0,
      lineHeight: 1.3,
      whiteSpace: "pre-wrap",
    },
  },

  templateButtons: {
    display: "flex",
    flexDirection: "column",
    gap: 1,
    margin: "7px -5px -1px",
    overflow: "hidden",
    borderRadius: "0 0 7px 7px",
    backgroundColor: theme.palette.mode === "dark"
      ? "rgba(255, 255, 255, 0.06)"
      : "rgba(0, 0, 0, 0.08)",
  },

  templateButton: {
    justifyContent: "center",
    minHeight: 32,
    padding: "6px 12px",
    borderRadius: 0,
    backgroundColor: theme.palette.mode === "dark" ? "#005c4b" : "#d9fdd3",
    color: "#008069",
    fontSize: "0.86rem",
    fontWeight: 500,
    lineHeight: 1.2,
    textTransform: "none",
    "&:hover": {
      backgroundColor: theme.palette.mode === "dark" ? "#066b58" : "#cdf4c8",
    },
  },

  templateImageBubble: {
    minWidth: 0,
    width: "fit-content",
    maxWidth: "min(380px, 82vw)",
    padding: "5px 7px",
    [theme.breakpoints.down("sm")]: {
      maxWidth: "84vw",
    },
  },

  templateMediaHeader: {
    margin: "-1px -3px 6px",
    overflow: "hidden",
    borderRadius: 8,
    "& > div": {
      display: "block",
      maxWidth: "100%",
      width: "100%",
    },
    "& img": {
      width: "100% !important",
      maxWidth: "100%",
      borderRadius: 8,
      cursor: "pointer",
    },
  },

  interactiveHeader: {
    fontWeight: 700,
    marginBottom: 4,
    wordBreak: "normal",
    overflowWrap: "anywhere",
  },

  interactiveFooter: {
    color: theme.palette.mode === "dark"
      ? "rgba(233, 237, 239, 0.72)"
      : "rgba(0, 0, 0, 0.58)",
    fontSize: "0.82rem",
    marginTop: 6,
    wordBreak: "normal",
    overflowWrap: "anywhere",
  },

  textContentItemDeleted: {
    display: "flex",
    flexDirection: "column",
    gap: 4,
    color: theme.palette.mode === "dark"
      ? "rgba(233, 237, 239, 0.6)"
      : "rgba(0, 0, 0, 0.45)",
    wordBreak: "normal",
    overflowWrap: "anywhere",
    padding: "0",
  },

  deletedMessageHeader: {
    display: "flex",
    alignItems: "center",
    gap: 4,
    fontStyle: "italic",
  },

  deletedMessageBody: {
    whiteSpace: "pre-wrap",
    opacity: 0.78,
  },

  messageMedia: {
    objectFit: "cover",
    width: "min(320px, 100%)",
    maxWidth: "100%",
    height: "auto",
    borderTopLeftRadius: 8,
    borderTopRightRadius: 8,
    borderBottomLeftRadius: 8,
    borderBottomRightRadius: 8,
    display: "block",
  },

  timestamp: {
    fontSize: "0.65rem",
    color: theme.palette.mode === 'dark' ? "rgba(255, 255, 255, 0.6)" : "rgba(0, 0, 0, 0.45)",
    marginLeft: "auto",
    display: "flex",
    alignItems: "center",
    gap: 3,
    justifyContent: "flex-end",
    lineHeight: 1,
    marginTop: 1,
    whiteSpace: "nowrap",
  },

  dailyTimestamp: {
    alignItems: "center",
    textAlign: "center",
    alignSelf: "center",
    width: "auto",
    maxWidth: "80%",
    margin: "10px auto",
    backgroundColor: theme.palette.mode === 'dark' ? "#2a3942" : "#e1f3fb",
    borderRadius: "16px",
    padding: "4px 12px",
  },

  dailyTimestampText: {
    color: theme.palette.mode === 'dark' ? "#e9edef" : "#667781",
    padding: "4px 0",
    alignSelf: "center",
    fontSize: "0.75rem",
  },

  ackIcons: {
    fontSize: "0.9rem",
    color: theme.palette.mode === 'dark' ? "rgba(255, 255, 255, 0.6)" : "rgba(0, 0, 0, 0.45)",
  },

  deletedIcon: {
    fontSize: "0.9rem",
    verticalAlign: "middle",
    marginRight: 4,
  },

  ackDoneAllIcon: {
    color: "#34b7f1",
    fontSize: "0.9rem",
  },

  ackFailedIcon: {
    color: theme.palette.error.main,
    fontSize: "0.9rem",
  },

  downloadMedia: {
    display: "flex",
    alignItems: "center",
    width: "auto",
    minWidth: 0,
    maxWidth: "100%",
    padding: "4px 0 2px",
    [theme.breakpoints.down("sm")]: {
      minWidth: 0,
      maxWidth: "100%",
    },
  },

  documentCard: {
    display: "flex",
    alignItems: "center",
    gap: 12,
    width: "min(320px, 100%)",
    maxWidth: "100%",
    borderRadius: 12,
    backgroundColor: theme.palette.mode === "dark" ? "rgba(255,255,255,0.06)" : "#f5f6f6",
    padding: "10px 12px",
    boxSizing: "border-box",
  },

  documentCardOutgoing: {
    backgroundColor: theme.palette.mode === "dark" ? "rgba(255,255,255,0.06)" : "#c9f2df",
    boxShadow: theme.palette.mode === "dark" ? "none" : "inset 0 0 0 1px rgba(0, 168, 132, 0.12)",
  },

  documentCardIncoming: {
    backgroundColor: theme.palette.mode === "dark" ? "rgba(255,255,255,0.06)" : "#f1f4f6",
    boxShadow: theme.palette.mode === "dark" ? "none" : "inset 0 0 0 1px rgba(17, 27, 33, 0.05)",
  },

  documentPreviewIconWrap: {
    width: 44,
    height: 44,
    borderRadius: 10,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
    backgroundColor: "#fce8e6",
    color: "#d93025",
  },

  documentPreviewIconWrapOutgoing: {
    backgroundColor: theme.palette.mode === "dark" ? "#fce8e6" : "#ecfff5",
    boxShadow: theme.palette.mode === "dark" ? "none" : "inset 0 0 0 1px rgba(0, 168, 132, 0.14)",
  },

  documentPreviewIconWrapIncoming: {
    backgroundColor: theme.palette.mode === "dark" ? "#fce8e6" : "#fff3f1",
  },

  documentPreviewIconWrapGeneric: {
    backgroundColor: theme.palette.mode === "dark" ? "#3b4a54" : "#e9edef",
    color: theme.palette.mode === "dark" ? "#d1d7db" : "#54656f",
  },

  documentPreviewIcon: {
    fontSize: "1.7rem",
  },

  documentInfo: {
    minWidth: 0,
    flex: 1,
    display: "flex",
    flexDirection: "column",
    gap: 2,
  },

  documentName: {
    fontSize: "0.94rem",
    fontWeight: 500,
    lineHeight: 1.25,
    color: theme.palette.mode === "dark" ? "#e9edef" : "#111b21",
    overflow: "hidden",
    display: "-webkit-box",
    WebkitLineClamp: 2,
    WebkitBoxOrient: "vertical",
    wordBreak: "break-word",
  },

  documentMeta: {
    fontSize: "0.74rem",
    color: theme.palette.mode === "dark" ? "#aebac1" : "#667781",
    textTransform: "uppercase",
    letterSpacing: "0.02em",
  },

  documentMetaOutgoing: {
    color: theme.palette.mode === "dark" ? "#aebac1" : "#2f6f62",
  },

  documentMetaIncoming: {
    color: theme.palette.mode === "dark" ? "#aebac1" : "#6b7b86",
  },

  documentDownloadButton: {
    width: 38,
    height: 38,
    flexShrink: 0,
    border: theme.palette.mode === "dark"
      ? "1px solid rgba(255,255,255,0.12)"
      : "1px solid rgba(17,27,33,0.14)",
    backgroundColor: theme.palette.mode === "dark" ? "#233138" : "#ffffff",
    color: theme.palette.mode === "dark" ? "#d1d7db" : "#667781",
    "&:hover": {
      backgroundColor: theme.palette.mode === "dark" ? "#2a3942" : "#f0f2f5",
    },
  },

  documentDownloadButtonOutgoing: {
    border: theme.palette.mode === "dark"
      ? "1px solid rgba(255,255,255,0.12)"
      : "1px solid rgba(0, 168, 132, 0.18)",
    backgroundColor: theme.palette.mode === "dark" ? "#233138" : "#f4fffa",
    color: theme.palette.mode === "dark" ? "#d1d7db" : "#1f7a63",
    "&:hover": {
      backgroundColor: theme.palette.mode === "dark" ? "#2a3942" : "#e5fbf2",
    },
  },

  documentDownloadButtonIncoming: {
    border: theme.palette.mode === "dark"
      ? "1px solid rgba(255,255,255,0.12)"
      : "1px solid rgba(17,27,33,0.10)",
    backgroundColor: theme.palette.mode === "dark" ? "#233138" : "#ffffff",
    color: theme.palette.mode === "dark" ? "#d1d7db" : "#5f6f7a",
  },

  reactionBadge: {
    position: "absolute",
    bottom: -16,
    right: 8,
    display: "inline-flex",
    alignItems: "center",
    gap: 2,
    minHeight: 22,
    padding: "1px 7px",
    borderRadius: 11,
    backgroundColor: theme.palette.mode === "dark" ? "#202c33" : "#ffffff",
    boxShadow: "0 1px 2px rgba(0, 0, 0, 0.18)",
    fontSize: "0.85rem",
    zIndex: 2,
  },

  reactionBadgeLeft: {
    left: 8,
    right: "auto",
  },

  reactionCount: {
    marginLeft: 2,
    fontSize: "0.72rem",
    color: theme.palette.mode === "dark" ? "#aebac1" : "#667781",
  },

  messageWithReactions: {
    marginBottom: 18,
  },
}));

const reducer = (state, action) => {
  if (action.type === "LOAD_MESSAGES") {
    const messages = action.payload;
    const newMessages = [];

    messages.forEach((message) => {
      const messageIndex = state.findIndex((m) => m.id === message.id);
      if (messageIndex !== -1) {
        state[messageIndex] = message;
      } else {
        newMessages.push(message);
      }
    });

    return [...newMessages, ...state];
  }

  if (action.type === "ADD_MESSAGE") {
    const newMessage = action.payload;
    const messageIndex = state.findIndex((m) => m.id === newMessage.id);

    if (messageIndex !== -1) {
      state[messageIndex] = newMessage;
    } else {
      state.push(newMessage);
    }

    return [...state];
  }

  if (action.type === "UPDATE_MESSAGE") {
    const messageToUpdate = action.payload;
    const messageIndex = state.findIndex((m) => m.id === messageToUpdate.id);

    if (messageIndex !== -1) {
      state[messageIndex] = messageToUpdate;
    }

    return [...state];
  }

  if (action.type === "RESET") {
    return [];
  }
};

const MessagesList = ({
  ticketId,
  isGroup,
  preview,
  selectedMessages,
  setSelectedMessages,
  onSendMessageToAI,
  onForwardMessage,
  onHighlightedMessagesChange,
  onMediaMessagesChange,
}) => {
  const classes = useStyles();
  const { setReplyingMessage } = useContext(ReplyMessageContext);
  const { socket, syncVersion } = useSocket();

  const [messagesList, dispatch] = useReducer(reducer, []);
  const [pageNumber, setPageNumber] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(false);
  const [focusedMessageId, setFocusedMessageId] = useState(null);
  const messagesListRef = useRef(null);
  const lastMessageRef = useRef();
  const messageRefs = useRef({});
  const shouldScrollToBottomOnLoad = useRef(false);
  const latestRequestKeyRef = useRef("");

  const [selectedMessage, setSelectedMessage] = useState({});
  const [anchorEl, setAnchorEl] = useState(null);
  const [anchorPosition, setAnchorPosition] = useState(null);
  const [highlightedMessages, setHighlightedMessages] = useState([]);
  const [pendingJumpMessageId, setPendingJumpMessageId] = useState(null);
  const messageOptionsMenuOpen = Boolean(anchorEl || anchorPosition);
  const currentTicketId = useRef(ticketId);
  const currentTicketDbId = useRef(null);

  const focusMessage = (messageId) => {
    const target = messageRefs.current[messageId];
    if (!target) {
      return false;
    }

    target.scrollIntoView({ behavior: "smooth", block: "center" });
    setFocusedMessageId(messageId);
    window.setTimeout(() => {
      setFocusedMessageId((prev) => (prev === messageId ? null : prev));
    }, 1500);

    return true;
  };

  const requestJumpToMessage = (messageId) => {
    if (!messageId) return;
    const found = focusMessage(messageId);

    if (found) {
      setPendingJumpMessageId(null);
      return;
    }

    if (hasMore) {
      setPendingJumpMessageId(messageId);
      return;
    }

    toast.info("No se encontro el mensaje en la vista actual.");
  };

  useEffect(() => {
    let mounted = true;

    const loadHighlights = async () => {
      try {
        const { data } = await api.get(`/messages/${ticketId}/highlights`);
        if (mounted) {
          setHighlightedMessages(Array.isArray(data) ? data : []);
        }
      } catch (error) {
        if (mounted) {
          setHighlightedMessages([]);
        }
      }
    };

    loadHighlights();

    return () => {
      mounted = false;
    };
  }, [ticketId]);

  useEffect(() => {
    if (onHighlightedMessagesChange) {
      onHighlightedMessagesChange(highlightedMessages);
    }
  }, [highlightedMessages, onHighlightedMessagesChange]);

  useEffect(() => {
    if (!onMediaMessagesChange) return;

    const mediaEntries = messagesList
      .filter((message) => {
        if (message.mediaUrl) return true;
        return ["image", "video", "audio", "ptt", "document", "sticker"].includes(
          message.mediaType
        );
      })
      .map((message) => ({
        id: message.id,
        body: message.body,
        mediaType: message.mediaType,
        mediaUrl: message.mediaUrl,
        fromMe: message.fromMe,
        createdAt: message.createdAt,
        contactName: message.contact?.name,
      }))
      .sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0));

    onMediaMessagesChange(mediaEntries);
  }, [messagesList, onMediaMessagesChange]);

  useEffect(() => {
    const handleJumpToMessage = (event) => {
      const { detail } = event;
      if (!detail || String(detail.ticketId) !== String(ticketId) || !detail.messageId) {
        return;
      }

      requestJumpToMessage(detail.messageId);
    };

    window.addEventListener("jump-to-message", handleJumpToMessage);
    return () => {
      window.removeEventListener("jump-to-message", handleJumpToMessage);
    };
  }, [ticketId, hasMore]);

  useEffect(() => {
    if (!pendingJumpMessageId || loading) {
      return;
    }

    const found = focusMessage(pendingJumpMessageId);
    if (found) {
      setPendingJumpMessageId(null);
      return;
    }

    if (hasMore) {
      loadMore();
      return;
    }

    toast.info("No se encontro el mensaje citado.");
    setPendingJumpMessageId(null);
  }, [pendingJumpMessageId, messagesList, hasMore, loading]);

  const handleSelectMessage = (message) => {
    if (!setSelectedMessages) return;
    setSelectedMessages(prev => {
      const isSelected = prev.some(m => m.id === message.id);
      if (isSelected) {
        return prev.filter(m => m.id !== message.id);
      } else {
        return [...prev, message];
      }
    });
  };

  useEffect(() => {
    dispatch({ type: "RESET" });
    setPageNumber(1);
    setHasMore(false);
    setLoading(false);
    messageRefs.current = {};
    setFocusedMessageId(null);
    setPendingJumpMessageId(null);
    shouldScrollToBottomOnLoad.current = true;
    latestRequestKeyRef.current = `${ticketId}:1:${preview ? "preview" : "full"}`;

    currentTicketId.current = ticketId;
    currentTicketDbId.current = null;
  }, [ticketId, preview, syncVersion]);

  useEffect(() => {
    const requestKey = `${ticketId}:${pageNumber}:${preview ? "preview" : "full"}`;
    latestRequestKeyRef.current = requestKey;

    setLoading(true);
    const delayDebounceFn = setTimeout(() => {
      const fetchMessages = async () => {
        try {
          const { data } = await api.get("/messages/" + ticketId, {
            params: { pageNumber, preview },
          });

          if (
            currentTicketId.current === ticketId &&
            latestRequestKeyRef.current === requestKey
          ) {
            dispatch({ type: "LOAD_MESSAGES", payload: data.messages });
            currentTicketDbId.current = data.ticket?.id || null;
            setHasMore(data.hasMore);
            setLoading(false);
          }

        } catch (err) {
          if (latestRequestKeyRef.current === requestKey) {
            setLoading(false);
            toastError(err);
          }
        }
      };
      fetchMessages();
    }, 500);
    return () => {
      clearTimeout(delayDebounceFn);
    };
  }, [pageNumber, ticketId, preview]);

  useEffect(() => {
    if (!shouldScrollToBottomOnLoad.current || pageNumber !== 1) {
      return;
    }

    if (!messagesList.length) {
      return;
    }

    requestAnimationFrame(() => {
      scrollToBottom("auto");
      shouldScrollToBottomOnLoad.current = false;
    });
  }, [messagesList, pageNumber]);

  useEffect(() => {
    if (!socket) {
      return undefined;
    }

    const handleConnect = () => socket.emit("joinChatBox", ticketId);

    const handleAppMessage = (data) => {
      const routeTicketId = currentTicketId.current;
      const dbTicketId = currentTicketDbId.current;
      const eventDbTicketId = data?.message?.ticketId || data?.ticket?.id || null;
      const eventRouteTicketId = data?.ticket?.publicId || data?.ticket?.id || null;

      const isCurrentTicketEvent =
        (dbTicketId && eventDbTicketId && String(dbTicketId) === String(eventDbTicketId)) ||
        (eventRouteTicketId && String(routeTicketId) === String(eventRouteTicketId));

      if (!isCurrentTicketEvent) {
        return;
      }

      if (data.action === "create") {
        dispatch({ type: "ADD_MESSAGE", payload: data.message });
        requestAnimationFrame(() => {
          scrollToBottom("smooth");
        });
      }

      if (data.action === "update") {
        dispatch({ type: "UPDATE_MESSAGE", payload: data.message });
      }
    };

    if (socket.connected) {
      socket.emit("joinChatBox", ticketId);
    }

    socket.on("connect", handleConnect);
    socket.on("appMessage", handleAppMessage);

    return () => {
      socket.emit("leaveChatBox", ticketId);
      socket.off("connect", handleConnect);
      socket.off("appMessage", handleAppMessage);
    };
  }, [socket, ticketId]);

  const loadMore = () => {
    setLoading(true);
    setPageNumber((prevPageNumber) => prevPageNumber + 1);
  };

  const scrollToBottom = (behavior = "smooth") => {
    if (messagesListRef.current) {
      messagesListRef.current.scrollTo({
        top: messagesListRef.current.scrollHeight,
        behavior,
      });
      return;
    }

    if (lastMessageRef.current) {
      lastMessageRef.current.scrollIntoView({ behavior, block: "end" });
    }
  };

  const handleScroll = (e) => {
    if (!hasMore) return;
    const { scrollTop } = e.currentTarget;

    if (scrollTop === 0) {
      e.currentTarget.scrollTop = 1;
    }

    if (loading) {
      return;
    }

    if (scrollTop < 50) {
      loadMore();
    }
  };

  const handleOpenMessageOptionsMenu = (e, message, useContextMenu = false) => {
    if (useContextMenu) {
      e.preventDefault();
      setAnchorEl(null);
      setAnchorPosition({ top: e.clientY, left: e.clientX });
    } else {
      setAnchorPosition(null);
      setAnchorEl(e.currentTarget);
    }
    setSelectedMessage(message);
  };

  const handleCloseMessageOptionsMenu = (e) => {
    setAnchorEl(null);
    setAnchorPosition(null);
  };

  const handleReplyMessage = (message) => {
    setReplyingMessage(message);
  };

  const handleCopyMessage = async (message) => {
    const textBody = (message?.body || "").trim();
    const fallbackText = message?.mediaUrl
      ? `Mensaje multimedia: ${message.mediaUrl}`
      : "Mensaje sin contenido de texto";
    const textToCopy = textBody || fallbackText;

    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(textToCopy);
      } else {
        const textArea = document.createElement("textarea");
        textArea.value = textToCopy;
        textArea.style.position = "fixed";
        textArea.style.opacity = "0";
        document.body.appendChild(textArea);
        textArea.select();
        document.execCommand("copy");
        document.body.removeChild(textArea);
      }
      toast.success("Mensaje copiado");
    } catch (error) {
      toast.error("No se pudo copiar el mensaje");
    }
  };

  const handleCopySelectedText = async () => {
    const selection = window.getSelection?.();
    const selectedText = selection ? selection.toString().trim() : "";

    if (!selectedText) {
      toast.info("No hay texto seleccionado");
      return;
    }

    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(selectedText);
      } else {
        const textArea = document.createElement("textarea");
        textArea.value = selectedText;
        textArea.style.position = "fixed";
        textArea.style.opacity = "0";
        document.body.appendChild(textArea);
        textArea.select();
        document.execCommand("copy");
        document.body.removeChild(textArea);
      }
      toast.success("Selección copiada");
    } catch (error) {
      toast.error("No se pudo copiar la selección");
    }
  };

  const handleReplyWithAI = (message) => {
    if (onSendMessageToAI) {
      onSendMessageToAI(message);
      return;
    }

    setReplyingMessage(message);
    const sourceText = (message?.body || "").trim() || "mensaje multimedia";
    const suggestion = `Reescribe una respuesta breve, profesional y empatica para este mensaje: "${sourceText}"`;

    window.dispatchEvent(
      new CustomEvent("ai-reply-suggestion", {
        detail: {
          ticketId,
          messageId: message?.id,
          suggestion,
        },
      })
    );

    toast.info("Sugerencia IA cargada en el cuadro de texto");
  };

  const handleForwardMessage = (message) => {
    if (onForwardMessage) {
      onForwardMessage(message);
    }
  };

  const serializeHighlightedMessage = (message) => ({
    id: message.id,
    body: message.body,
    mediaType: message.mediaType,
    mediaUrl: message.mediaUrl,
    fromMe: message.fromMe,
    createdAt: message.createdAt,
    contactName: message.contact?.name,
  });

  const handleToggleHighlight = async (message) => {
    const exists = highlightedMessages.some((item) => item.id === message.id);

    try {
      if (exists) {
        await api.delete(`/messages/${ticketId}/highlights/${message.id}`);
        setHighlightedMessages((prev) => prev.filter((item) => item.id !== message.id));
        toast.info("Mensaje sin destacar");
        return;
      }

      await api.post(`/messages/${ticketId}/highlights`, { messageId: message.id });
      setHighlightedMessages((prev) => [...prev, serializeHighlightedMessage(message)]);
      toast.success("Mensaje destacado");
    } catch (error) {
      toast.error("No se pudo actualizar el destacado");
    }
  };

  const handleReactMessage = async (message, emoji) => {
    try {
      const { data } = await api.post(`/messages/${ticketId}/${message.id}/reaction`, {
        emoji,
      });
      dispatch({ type: "UPDATE_MESSAGE", payload: data });
    } catch (error) {
      toastError(error);
    }
  };

  const isMessageHighlighted = (messageId) =>
    highlightedMessages.some((message) => message.id === messageId);

  const parseReactions = (message) => {
    const reactions = message?.reactions;
    if (!reactions) return [];
    if (Array.isArray(reactions)) return reactions;

    try {
      const parsed = JSON.parse(reactions);
      return Array.isArray(parsed) ? parsed : [];
    } catch (error) {
      return [];
    }
  };

  const renderMessageReactions = (message) => {
    const reactions = parseReactions(message).filter((reaction) => reaction?.emoji);
    if (!reactions.length) return null;

    const emojis = [...new Set(reactions.map((reaction) => reaction.emoji))].join("");

    return (
      <span
        className={clsx(classes.reactionBadge, {
          [classes.reactionBadgeLeft]: !message.fromMe,
        })}
      >
        {emojis}
        {reactions.length > 1 && (
          <span className={classes.reactionCount}>{reactions.length}</span>
        )}
      </span>
    );
  };

  const isFileCardMessage = (message) => {
    if (!message?.mediaUrl) return false;
    if (isTemplateImageHeaderMessage(message)) return false;

    return !["image", "video", "audio", "ptt", "location", "vcard", "sticker"].includes(
      message.mediaType
    );
  };

  const getDocumentPresentation = (message) => {
    const fileName = (message.body || "").trim() || "Documento";
    const extensionFromName = fileName.includes(".")
      ? fileName.split(".").pop()
      : "";
    const extensionFromUrl = message.mediaUrl?.split("?")[0]?.split(".").pop?.() || "";
    const extension = (extensionFromName || extensionFromUrl || "file").toUpperCase();
    const isPdf = extension === "PDF";

    return {
      fileName,
      extension,
      isPdf,
    };
  };

  const renderDocumentCard = (message) => {
    const { fileName, extension, isPdf } = getDocumentPresentation(message);
    const isOutgoing = Boolean(message.fromMe);

    return (
      <>
        <div className={classes.downloadMedia}>
          <div
            className={clsx(classes.documentCard, {
              [classes.documentCardOutgoing]: isOutgoing,
              [classes.documentCardIncoming]: !isOutgoing,
            })}
          >
            <div
              className={clsx(classes.documentPreviewIconWrap, {
                [classes.documentPreviewIconWrapOutgoing]: isOutgoing,
                [classes.documentPreviewIconWrapIncoming]: !isOutgoing,
                [classes.documentPreviewIconWrapGeneric]: !isPdf,
              })}
            >
              {isPdf ? (
                <PictureAsPdf className={classes.documentPreviewIcon} />
              ) : (
                <InsertDriveFile className={classes.documentPreviewIcon} />
              )}
            </div>
            <div className={classes.documentInfo}>
              <span className={classes.documentName}>{fileName}</span>
              <span
                className={clsx(classes.documentMeta, {
                  [classes.documentMetaOutgoing]: isOutgoing,
                  [classes.documentMetaIncoming]: !isOutgoing,
                })}
              >
                {extension} · Documento
              </span>
            </div>
            <IconButton
              component="a"
              href={message.mediaUrl}
              target="_blank"
              rel="noreferrer"
              download={fileName || true}
              className={clsx(classes.documentDownloadButton, {
                [classes.documentDownloadButtonOutgoing]: isOutgoing,
                [classes.documentDownloadButtonIncoming]: !isOutgoing,
              })}
              size="small"
            >
              <GetApp fontSize="small" />
            </IconButton>
          </div>
        </div>
        <Divider />
      </>
    );
  };

  const checkMessageMedia = (message) => {
    if (message.mediaType === "location" && message.body.split('|').length >= 2) {
      let locationParts = message.body.split('|')
      let imageLocation = locationParts[0]
      let linkLocation = locationParts[1]

      let descriptionLocation = null

      if (locationParts.length > 2)
        descriptionLocation = message.body.split('|')[2]

      return <LocationPreview image={imageLocation} link={linkLocation} description={descriptionLocation} />
    }
    else if (message.mediaType === "vcard") {
      let array = message.body.split("\n");
      let obj = [];
      let contact = "";
      for (let index = 0; index < array.length; index++) {
        const v = array[index];
        let values = v.split(":");
        for (let ind = 0; ind < values.length; ind++) {
          if (values[ind].indexOf("+") !== -1) {
            obj.push({ number: values[ind] });
          }
          if (values[ind].indexOf("FN") !== -1) {
            contact = values[ind + 1];
          }
        }
      }
      return <VcardPreview contact={contact} numbers={obj[0]?.number} />
    }
    else if (isTemplateImageHeaderMessage(message)) {
      return (
        <div className={classes.templateMediaHeader}>
          <ModalImageCors
            imageUrl={message.mediaUrl}
            fileName={message.body}
            galleryItems={imageGalleryItems}
            currentImageId={message.id}
          />
        </div>
      );
    }
    else if (message.mediaType === "image") {
      return (
        <ModalImageCors
          imageUrl={message.mediaUrl}
          fileName={message.body}
          galleryItems={imageGalleryItems}
          currentImageId={message.id}
        />
      );
    } else if (message.mediaType === "audio" || message.mediaType === "ptt") {
      return <Audio url={message.mediaUrl} />
    } else if (message.mediaType === "video") {
      return <ModalImageCors imageUrl={message.mediaUrl} mediaType="video" fileName={message.body} />;
    } else if (isFileCardMessage(message)) {
      return renderDocumentCard(message);
    } else {
      return (
        <>
          <div className={classes.downloadMedia}>
            <Button
              startIcon={<GetApp />}
              color="primary"
              variant="outlined"
              target="_blank"
              href={message.mediaUrl}
              download={message.body || true}
            >
              {message.body || "Download"}
            </Button>
          </div>
          <Divider />
        </>
      );
    }
  };

  const renderMessageAck = (message) => {
    if (message.ack === -1) {
      return <Block fontSize="small" className={classes.ackFailedIcon} />;
    }
    if (message.ack === 0) {
      return <AccessTime fontSize="small" className={classes.ackIcons} />;
    }
    if (message.ack === 1) {
      return <Done fontSize="small" className={classes.ackIcons} />;
    }
    if (message.ack === 2) {
      return <DoneAll fontSize="small" className={classes.ackIcons} />;
    }
    if (message.ack === 3 || message.ack === 4) {
      return <DoneAll fontSize="small" className={classes.ackDoneAllIcon} />;
    }
  };

  const renderDailyTimestamps = (message, index) => {
    if (index === 0) {
      return (
        <span
          className={classes.dailyTimestamp}
          key={`timestamp-${message.id}`}
        >
          <div className={classes.dailyTimestampText}>
            {format(parseISO(messagesList[index].createdAt), "dd/MM/yyyy")}
          </div>
        </span>
      );
    }
    if (index < messagesList.length - 1) {
      let messageDay = parseISO(messagesList[index].createdAt);
      let previousMessageDay = parseISO(messagesList[index - 1].createdAt);

      if (!isSameDay(messageDay, previousMessageDay)) {
        return (
          <span
            className={classes.dailyTimestamp}
            key={`timestamp-${message.id}`}
          >
            <div className={classes.dailyTimestampText}>
              {format(parseISO(messagesList[index].createdAt), "dd/MM/yyyy")}
            </div>
          </span>
        );
      }
    }
    if (index === messagesList.length - 1) {
      return (
        <div
          key={`ref-${message.createdAt}`}
          ref={lastMessageRef}
          style={{ float: "left", clear: "both" }}
        />
      );
    }
  };

  const renderMessageDivider = (message, index) => {
    if (index < messagesList.length && index > 0) {
      let messageUser = messagesList[index].fromMe;
      let previousMessageUser = messagesList[index - 1].fromMe;

      if (messageUser !== previousMessageUser) {
        return (
          <span style={{ marginTop: 16 }} key={`divider-${message.id}`}></span>
        );
      }
    }
  };

  const renderQuotedMessage = (message) => {
    const quoted = message.quotedMsg;
    const quotedMessageId = quoted?.id || message.quotedMsgId;
    const cleanQuotedBody = (body = "") =>
      body
        .replace(/^\u200B\u200B\u200B/, "")
        .trim()
        .replace(/^\[(image|video|audio|ptt|sticker)\]$/i, "");

    const renderQuotedForwardedLabel = () =>
      quoted?.body?.startsWith("\u200B\u200B\u200B") ? (
        <span className={classes.quotedMediaText}>Reenviado</span>
      ) : null;

    const renderQuotedContent = () => {
      if (!quoted) return null;
      const quotedBody = cleanQuotedBody(quoted.body);

      if (quoted.mediaType === "image" && quoted.mediaUrl) {
        return (
          <>
            {renderQuotedForwardedLabel()}
            <img
              src={quoted.mediaUrl}
              alt={quoted.body || "quoted-image"}
              className={classes.quotedMediaThumb}
            />
            <span className={classes.quotedMediaText}>Imagen</span>
            {quotedBody && <span>{quotedBody}</span>}
          </>
        );
      }

      if (quoted.mediaType === "video") {
        return (
          <>
            {renderQuotedForwardedLabel()}
            <span className={classes.quotedMediaText}>Video</span>
            {quotedBody && <span>{quotedBody}</span>}
          </>
        );
      }

      if (quoted.mediaType === "audio" || quoted.mediaType === "ptt") {
        return (
          <>
            {renderQuotedForwardedLabel()}
            <span className={classes.quotedMediaText}>Audio</span>
            {quotedBody && <span>{quotedBody}</span>}
          </>
        );
      }

      if (quoted.mediaType === "document") {
        return (
          <>
            {renderQuotedForwardedLabel()}
            <span className={classes.quotedMediaText}>Documento</span>
            {quotedBody && <span>{quotedBody}</span>}
          </>
        );
      }

      if (quoted.mediaType === "sticker") {
        return (
          <>
            {renderQuotedForwardedLabel()}
            <span className={classes.quotedMediaText}>Sticker</span>
          </>
        );
      }

      return quoted.body;
    };

    return (
      <div
        className={clsx(classes.quotedContainerLeft, {
          [classes.quotedContainerRight]: message.fromMe,
        })}
        onClick={(event) => {
          event.stopPropagation();
          requestJumpToMessage(quotedMessageId);
        }}
      >
        <span
          className={clsx(classes.quotedSideColorLeft, {
            [classes.quotedSideColorRight]: message.quotedMsg?.fromMe,
          })}
        ></span>
        <div className={classes.quotedMsg}>
          {!quoted?.fromMe && (
            <span className={classes.messageContactName}>
              {quoted?.contact?.name}
            </span>
          )}
          {renderQuotedContent()}
        </div>
      </div>
    );
  };

  const hasVisualMedia = (message) =>
    message.mediaType === "image" ||
    message.mediaType === "video" ||
    message.mediaType === "sticker" ||
    isTemplateImageHeaderMessage(message);

  const shouldRenderMessageBody = (message) => {
    if (isFileCardMessage(message)) return false;
    if (!message.body) return false;
    if (!message.mediaUrl) return true;

    const body = message.body.trim();
    const bodyWithoutForwardedMarker = body.replace(/^\u200B\u200B\u200B/, "").trim();
    const mediaFilename = message.mediaUrl.split("?")[0].split("/").pop();
    const hiddenMediaBodies = ["[image]", "[video]", "[audio]", "[ptt]", "[sticker]"];
    const mediaFilenameBody = /\.(jpe?g|png|gif|webp|mp4|mov|webm|mp3|ogg|oga|wav|m4a)$/i.test(
      bodyWithoutForwardedMarker
    );

    if (!bodyWithoutForwardedMarker && body.startsWith("\u200B\u200B\u200B")) return true;
    if (hiddenMediaBodies.includes(bodyWithoutForwardedMarker.toLowerCase())) {
      return body.startsWith("\u200B\u200B\u200B");
    }
    if (mediaFilename && mediaFilename === bodyWithoutForwardedMarker) {
      return body.startsWith("\u200B\u200B\u200B");
    }
    if (["image", "audio", "ptt", "video", "sticker"].includes(message.mediaType)) {
      return !mediaFilenameBody;
    }

    return true;
  };

  const renderDeletedMessage = (message) => (
    <div
      className={clsx(classes.textContentItem, {
        [classes.textContentItemDeleted]: message.isDeleted,
      })}
    >
      <div className={classes.deletedMessageHeader}>
        <Block
          color="disabled"
          fontSize="small"
          className={classes.deletedIcon}
        />
        <span>Mensaje eliminado</span>
      </div>
      {message.body ? (
        <span className={classes.deletedMessageBody}>{message.body}</span>
      ) : null}
      <span className={classes.timestamp}>
        {format(parseISO(message.createdAt), "HH:mm")}
        {message.fromMe && renderMessageAck(message)}
      </span>
    </div>
  );

  const getSignedBodyParts = (body = "") => {
    const rawBody = String(body || "");
    const isForwarded = rawBody.startsWith("\u200B\u200B\u200B");
    const normalizedBody = rawBody.replace(/^\u200B\u200B\u200B/, "").trim();
    const hideMediaPlaceholder = text =>
      /^\[(image|video|audio|ptt|sticker|archivo)\]$/i.test(text.trim()) ? "" : text;
    const signedBodyMatch = normalizedBody.match(/^(\*[^*\n]+:\*)\s*\n?([\s\S]*)$/);

    if (!signedBodyMatch) {
      return {
        isForwarded,
        signature: "",
        text: hideMediaPlaceholder(normalizedBody),
      };
    }

    return {
      isForwarded,
      signature: signedBodyMatch[1],
      text: hideMediaPlaceholder(signedBodyMatch[2].trim()),
    };
  };

  const getTemplateComponents = (message) => {
    const components = message?.templateComponents;
    if (!components) return null;

    if (typeof components !== "string") return components;

    try {
      return JSON.parse(components);
    } catch (error) {
      return null;
    }
  };

  const renderInteractiveHeader = (message) => {
    const components = getTemplateComponents(message);
    if (!components?.headerText) return null;

    return (
      <div className={classes.interactiveHeader}>
        {components.headerText}
      </div>
    );
  };

  const renderInteractiveFooter = (message) => {
    const components = getTemplateComponents(message);
    if (!components?.footerText) return null;

    return (
      <div className={classes.interactiveFooter}>
        {components.footerText}
      </div>
    );
  };

  const isTemplateImageHeaderMessage = (message) => {
    const components = getTemplateComponents(message);
    const header = components?.header;

    return (
      Boolean(message?.mediaUrl) &&
      String(components?.type || "").toLowerCase() === "template" &&
      String(header?.format || "").toUpperCase() === "IMAGE"
    );
  };

  const imageGalleryItems = useMemo(
    () =>
      messagesList
        .filter(
          message =>
            !message.isDeleted &&
            message.mediaUrl &&
            (message.mediaType === "image" || isTemplateImageHeaderMessage(message))
        )
        .map(message => ({
          id: message.id,
          imageUrl: message.mediaUrl,
          fileName: message.body || message.mediaUrl,
        })),
    [messagesList]
  );

  const renderMessageBodyContent = (message) => (
    <>
      {message.quotedMsg && renderQuotedMessage(message)}
      {renderInteractiveHeader(message)}
      {shouldRenderMessageBody(message) && (
        <MarkdownWrapper>{message.body}</MarkdownWrapper>
      )}
      {renderInteractiveFooter(message)}
      {renderTemplateButtons(message)}
    </>
  );

  const getTemplateButtons = (message) => {
    const parsedComponents = getTemplateComponents(message);

    if (Array.isArray(parsedComponents?.buttons)) {
      return parsedComponents.buttons;
    }

    const buttonComponent = Array.isArray(parsedComponents)
      ? parsedComponents.find(component => component.type === "BUTTONS")
      : null;

    return Array.isArray(buttonComponent?.buttons) ? buttonComponent.buttons : [];
  };

  const renderTemplateButtons = (message) => {
    const buttons = getTemplateButtons(message)
      .filter(button => button?.text)
      .slice(0, 3);

    if (!buttons.length) return null;

    return (
      <div className={classes.templateButtons}>
        {buttons.map((button, index) => {
          const url = button.url && !/{{\s*\d+\s*}}/.test(button.url)
            ? button.url
            : undefined;

          return (
            <Button
              key={`${message.id}-template-button-${index}`}
              className={classes.templateButton}
              component={url ? "a" : "button"}
              href={url}
              target={url ? "_blank" : undefined}
              rel={url ? "noreferrer" : undefined}
              onClick={(event) => {
                if (!url) {
                  event.preventDefault();
                }
                event.stopPropagation();
              }}
            >
              {button.text}
            </Button>
          );
        })}
      </div>
    );
  };

  const renderMessageTimestamp = (message, showAck = false) => (
    <span className={classes.timestamp}>
      {format(parseISO(message.createdAt), "HH:mm")}
      {showAck && renderMessageAck(message)}
    </span>
  );

  const renderOutgoingMediaTopContent = (message) => {
    const { isForwarded, signature } = getSignedBodyParts(message.body);

    if (!isForwarded && !signature && !message.quotedMsg) return null;

    return (
      <div className={classes.textContentItem}>
        {isForwarded && <MarkdownWrapper>{"\u200B\u200B\u200B"}</MarkdownWrapper>}
        {signature && <MarkdownWrapper>{signature}</MarkdownWrapper>}
        {message.quotedMsg && renderQuotedMessage(message)}
      </div>
    );
  };

  const renderOutgoingMediaBottomContent = (message) => {
    const { text } = getSignedBodyParts(message.body);

    return (
      <div className={classes.textContentItem}>
        {text && <MarkdownWrapper>{text}</MarkdownWrapper>}
        {renderTemplateButtons(message)}
        {renderMessageTimestamp(message, true)}
      </div>
    );
  };

  const renderMessages = () => {
    if (messagesList.length > 0) {
      const viewMessagesList = messagesList.map((message, index) => {
        if (message.mediaType === "notification") {
          return (
            <div className={classes.dailyTimestamp} key={`notification-${message.id}`}>
              <div className={classes.dailyTimestampText}>
                <MarkdownWrapper>{message.body}</MarkdownWrapper>
              </div>
            </div>
          );
        }
        if (!message.fromMe) {
          return (
            <React.Fragment key={message.id}>
              {renderDailyTimestamps(message, index)}
              {renderMessageDivider(message, index)}
              <div style={{ display: "flex", alignItems: "center", alignSelf: "flex-start" }}>
                {selectedMessages && selectedMessages.length > 0 && (
                  <Checkbox
                    checked={selectedMessages.some(m => m.id === message.id)}
                    onChange={() => handleSelectMessage(message)}
                    color="primary"
                  />
                )}
                <div
                  className={clsx(classes.messageLeft, {
                    [classes.mediaMessage]: hasVisualMedia(message),
                    [classes.fileMessage]: isFileCardMessage(message),
                    [classes.templateImageBubble]: isTemplateImageHeaderMessage(message),
                    [classes.highlightedMessage]: isMessageHighlighted(message.id),
                    [classes.focusedMessage]: focusedMessageId === message.id,
                    [classes.messageWithReactions]: parseReactions(message).length > 0,
                  })}
                  ref={(element) => {
                    if (element) {
                      messageRefs.current[message.id] = element;
                    }
                  }}
                  onContextMenu={(e) => handleOpenMessageOptionsMenu(e, message, true)}
                  onDoubleClick={() => handleReplyMessage(message)}
                >
                  {isMessageHighlighted(message.id) && (
                    <Star className={classes.highlightedStar} />
                  )}
                  <IconButton
                    variant="contained"
                    size="small"
                    id="messageActionsButton"
                    disabled={message.isDeleted}
                    className={classes.messageActionsButton}
                    onClick={(e) => handleOpenMessageOptionsMenu(e, message)}
                  >
                    <ExpandMore />
                  </IconButton>
                  {isGroup && (
                    <span className={classes.messageContactName}>
                      {message.contact?.name}
                    </span>
                  )}
                  {!message.isDeleted && (message.mediaUrl || message.mediaType === "location" || message.mediaType === "vcard"
                  ) && checkMessageMedia(message)}
                  {message.isDeleted ? (
                    renderDeletedMessage(message)
                  ) : (
                    <div className={classes.textContentItem}>
                      {message.quotedMsg && renderQuotedMessage(message)}
                      {shouldRenderMessageBody(message) && (
                        <MarkdownWrapper>{message.body}</MarkdownWrapper>
                      )}
                      {renderTemplateButtons(message)}
                      <span className={classes.timestamp}>
                        {format(parseISO(message.createdAt), "HH:mm")}
                      </span>
                    </div>
                  )}
                  {renderMessageReactions(message)}
                </div>
              </div>
            </React.Fragment>
          );
        } else {
          const placeBodyAroundMedia =
            !message.isDeleted &&
            message.mediaUrl &&
            !isFileCardMessage(message) &&
            shouldRenderMessageBody(message);

          return (
            <React.Fragment key={message.id}>
              {renderDailyTimestamps(message, index)}
              {renderMessageDivider(message, index)}
              <div style={{ display: "flex", alignItems: "center", alignSelf: "flex-end" }}>
                {selectedMessages && selectedMessages.length > 0 && (
                  <Checkbox
                    checked={selectedMessages.some(m => m.id === message.id)}
                    onChange={() => handleSelectMessage(message)}
                    color="primary"
                  />
                )}
                <div
                  className={clsx(classes.messageRight, {
                    [classes.mediaMessage]: hasVisualMedia(message),
                    [classes.fileMessage]: isFileCardMessage(message),
                    [classes.templateImageBubble]: isTemplateImageHeaderMessage(message),
                    [classes.highlightedMessage]: isMessageHighlighted(message.id),
                    [classes.focusedMessage]: focusedMessageId === message.id,
                    [classes.messageWithReactions]: parseReactions(message).length > 0,
                  })}
                  ref={(element) => {
                    if (element) {
                      messageRefs.current[message.id] = element;
                    }
                  }}
                  onContextMenu={(e) => handleOpenMessageOptionsMenu(e, message, true)}
                  onDoubleClick={() => handleReplyMessage(message)}
                >
                  {isMessageHighlighted(message.id) && (
                    <Star className={classes.highlightedStar} />
                  )}
                  <IconButton
                    variant="contained"
                    size="small"
                    id="messageActionsButton"
                    disabled={message.isDeleted}
                    className={classes.messageActionsButton}
                    onClick={(e) => handleOpenMessageOptionsMenu(e, message)}
                  >
                    <ExpandMore />
                  </IconButton>
                  {placeBodyAroundMedia && renderOutgoingMediaTopContent(message)}
                  {!message.isDeleted && (message.mediaUrl || message.mediaType === "location" || message.mediaType === "vcard"
                  ) && checkMessageMedia(message)}
                  {message.isDeleted ? (
                    renderDeletedMessage(message)
                  ) : placeBodyAroundMedia ? (
                    renderOutgoingMediaBottomContent(message)
                  ) : (
                    <div
                      className={clsx(classes.textContentItem, {
                        [classes.textContentItemDeleted]: message.isDeleted,
                      })}
                    >
                      {renderMessageBodyContent(message)}
                      {renderMessageTimestamp(message, true)}
                    </div>
                  )}
                  {renderMessageReactions(message)}
                </div>
              </div>
            </React.Fragment>
          );
        }
      });
      return viewMessagesList;
    } else {
      return <div>Say hello to your new contact!</div>;
    }
  };

  return (
    <div className={classes.messagesListWrapper}>
      <MessageOptionsMenu
        message={selectedMessage}
        anchorEl={anchorEl}
        anchorPosition={anchorPosition}
        menuOpen={messageOptionsMenuOpen}
        handleClose={handleCloseMessageOptionsMenu}
        onSelectMessage={handleSelectMessage}
        onCopyMessage={handleCopyMessage}
        onCopySelectedText={handleCopySelectedText}
        onReplyWithAI={handleReplyWithAI}
        onForwardMessage={handleForwardMessage}
        onToggleHighlight={handleToggleHighlight}
        onReact={handleReactMessage}
        isHighlighted={isMessageHighlighted(selectedMessage?.id)}
      />
      <div
        id="messagesList"
        ref={messagesListRef}
        className={classes.messagesList}
        onScroll={handleScroll}
      >
        {messagesList.length > 0 ? renderMessages() : []}
      </div>
      {loading && (
        <div>
          <CircularProgress className={classes.circleLoading} />
        </div>
      )}
    </div>
  );
};

export default MessagesList;
